import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fadeUp, stagger } from "@/lib/motion";
import { HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "Are you an official TallyPrime authorized partner?",
    a: "Yes. We are an authorized TallyPrime distributor and implementation partner in India, with certified engineers and 20+ years of on-ground experience across 1500+ businesses.",
  },
  {
    q: "How do I choose between Silver, Gold and Rental plans?",
    a: "If you are a single user with straightforward billing, Silver is ideal. For teams with multiple users on a LAN and advanced payroll/inventory needs, Gold is recommended. Choose Rental if you'd like a low upfront, subscription-style plan.",
  },
  {
    q: "Do you help with GST returns and e-invoicing?",
    a: "Absolutely. TallyPrime supports GSTR-1, GSTR-3B, e-Way Bills and e-Invoicing out of the box. We configure it end-to-end and train your accounts team.",
  },
  {
    q: "Can we access TallyPrime remotely or on the cloud?",
    a: "Yes. We set up secure remote access and can host TallyPrime on our cloud environment with automated backups, multi-user sync and full compliance.",
  },
  {
    q: "What is included in the AMC?",
    a: "Our AMC covers unlimited support tickets, version upgrades, patches, health checks, minor customizations and priority phone/WhatsApp support with SLAs.",
  },
  {
    q: "Do you provide training for our finance team?",
    a: "Yes. We offer classroom, on-site and remote training modules across accounting, GST, payroll, inventory and reporting — tailored to your team's level.",
  },
];

export default function FAQ() {
  return (
    <section data-testid="faq-section" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Comprehensive Guide</motion.span>
          <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
            Questions, answered.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-[16px] text-[#4A5568]">
            Everything you need to know before working with us. Can't find the answer?{" "}
            <a href="#contact" className="text-[#0D47A1] font-semibold underline underline-offset-4">Talk to our team.</a>
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.05, 0.06)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-12 rounded-2xl bg-[#FAF9F6] border border-[#0D47A1]/10 p-2 sm:p-4 shadow-[0_8px_30px_rgba(13,71,161,0.06)]"
        >
          <Accordion type="single" collapsible defaultValue="item-0">
            {FAQS.map((faq, i) => (
              <motion.div key={faq.q} variants={fadeUp}>
                <AccordionItem
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-[#0D47A1]/8 last:border-none px-4"
                >
                  <AccordionTrigger className="text-left font-display font-bold text-[16px] text-[#1A1A2E] hover:no-underline py-5 [&[data-state=open]>svg]:text-[#FFA000]">
                    <span className="flex items-center gap-3">
                      <HelpCircle className="w-4 h-4 text-[#FFA000] shrink-0" />
                      {faq.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-[15px] leading-relaxed text-[#4A5568] pl-7">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
